import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SchuifpuiOptionsPanel from '../components/SchuifpuiOptionsPanel';
import DoorDrawingBoard from '../components/DoorDrawingBoard';
import Summary from '../components/Summary';
import ConfiguratorShell from '../components/ConfiguratorShell';
import { useTranslation } from '../context/TranslationContext';
import { 
  raamtypes, 
  profileSystems, 
  glassTextures, 
  glazingTypes, 
  colors, 
  woodJointOptions,
  PRICING 
} from '../data/mockData';
import {
  getSchuifpuiMinWidthMm,
  clampSchuifpuiDimensions,
  SCHUIFPUI_MIN_HEIGHT_MM,
  SCHUIFPUI_MAX_HEIGHT_MM,
  SCHUIFPUI_MAX_WIDTH_MM,
} from '../lib/pdfPricing';
import {
  trackFrameColorChange,
  DOOR_COLOR_FIELDS,
} from '../lib/configuratorColors';
import {
  applyPersistedConfiguratorPreferences,
  trackSharedConfiguratorChange,
} from '../lib/configuratorGlazing';
import { useConfiguratorDirtyState } from '../hooks/useConfiguratorDirtyState';
import { useConfiguratorLoadState } from '../hooks/useConfiguratorLoadState';
import {
  createDefaultAdditionalItems,
  withDefaultAdditionalItems,
  applyAdditionalItemsChange,
} from '../lib/additionalItems';

const DEFAULT_VENTILATOR_SLIDING = {
  left: false,
  right: false,
  centerLeft: false,
  centerRight: false,
  centerCenter: false,
  centerThird: false,
};

const DEFAULT_SCHUIFPUI_CONFIG = {
  width: 2000,
  height: 2000,
  profileSystem: 'schuco',
  slidingPosition: 'left',
  thresholdHeight: '30mm',
  leftGlassTexture: 'helder-glas',
  rightGlassTexture: 'helder-glas',
  rightGlazingType: 'double',
  interiorColor: 'ral-9016',
  exteriorColor: 'anthracite',
  ventilatorSliding: { ...DEFAULT_VENTILATOR_SLIDING },
  quantity: 1
};

const Schuifpui = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const drawingBoardRef = useRef(null);
  const saveCurrentRef = useRef(null);

  const buildDefaultConfig = () =>
    withDefaultAdditionalItems(
      applyPersistedConfiguratorPreferences(DEFAULT_SCHUIFPUI_CONFIG, DOOR_COLOR_FIELDS)
    );

  const { config, setConfig, navLoadToken, lastNavLoadedConfig } = useConfiguratorLoadState(
    location,
    buildDefaultConfig,
    (loaded) => withDefaultAdditionalItems(loaded)
  );
  const { isDirty, markClean, markDirty, resetBaseline } = useConfiguratorDirtyState(config);

  useEffect(() => {
    if (navLoadToken > 0 && lastNavLoadedConfig) {
      resetBaseline(lastNavLoadedConfig);
    }
  }, [navLoadToken, lastNavLoadedConfig, resetBaseline]);

  const handleReset = () => {
    const nextConfig = buildDefaultConfig();
    setConfig(nextConfig);
    resetBaseline(nextConfig);
  };

  const handleAdditionalItemsChange = (updater) => {
    markDirty();
    setConfig((prev) => applyAdditionalItemsChange(prev, updater));
  };

  const handleConfigChange = (field, value) => {
    trackFrameColorChange(field, value, DOOR_COLOR_FIELDS);
    trackSharedConfiguratorChange(field, value);
    markDirty();
    setConfig((prev) => {
      if (field === 'width') {
        const minWidth = getSchuifpuiMinWidthMm(prev.slidingPosition);
        const nextWidth = Math.max(
          minWidth,
          Math.min(SCHUIFPUI_MAX_WIDTH_MM, Math.round(Number(value) || prev.width))
        );
        return { ...prev, width: nextWidth };
      }
      if (field === 'height') {
        const nextHeight = Math.max(
          SCHUIFPUI_MIN_HEIGHT_MM,
          Math.min(SCHUIFPUI_MAX_HEIGHT_MM, Math.round(Number(value) || prev.height))
        );
        return { ...prev, height: nextHeight };
      }
      if (field === 'slidingPosition') {
        return clampSchuifpuiDimensions({ ...prev, slidingPosition: value });
      }
      return { ...prev, [field]: value };
    });
  };

  // Safety check - if we're not on this route, navigate away
  useEffect(() => {
    if (location.pathname !== '/schuifpui' && location.pathname !== '/configurator-select') {
      // Force navigation if somehow we're still mounted on wrong route
      navigate('/configurator-select', { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <ConfiguratorShell
      title={t('configurator.slidingDoorConfigurator')}
      drawingBoardRef={drawingBoardRef}
      onReset={handleReset}
      isDirty={isDirty}
      onMarkClean={markClean}
      saveCurrentRef={saveCurrentRef}
      previewCentered
      optionsPanel={
        <SchuifpuiOptionsPanel
          config={config}
          onConfigChange={handleConfigChange}
          additionalItems={config.additionalItems || createDefaultAdditionalItems()}
          onAdditionalItemsChange={handleAdditionalItemsChange}
        />
      }
      previewPanel={
        <DoorDrawingBoard
          width={config.width}
          height={config.height}
          doorType="sliding"
          slidingPosition={config.slidingPosition}
          interiorColor={config.interiorColor}
          exteriorColor={config.exteriorColor}
          leftGlassTexture="helder-glas"
          rightGlassTexture="helder-glas"
          onWidthChange={(value) => handleConfigChange('width', value)}
          onHeightChange={(value) => handleConfigChange('height', value)}
          minWidth={getSchuifpuiMinWidthMm(config.slidingPosition)}
          minHeight={SCHUIFPUI_MIN_HEIGHT_MM}
          maxWidth={SCHUIFPUI_MAX_WIDTH_MM}
          maxHeight={SCHUIFPUI_MAX_HEIGHT_MM}
          ventilatorSliding={config.ventilatorSliding}
          onVentilatorSlidingChange={(ventilatorSliding) =>
            handleConfigChange('ventilatorSliding', ventilatorSliding)
          }
        />
      }
      summaryPanel={
        <Summary
          config={config}
          pricing={PRICING}
          profileSystems={profileSystems}
          glassTextures={glassTextures}
          glazingTypes={glazingTypes}
          colors={colors}
          raamtypes={raamtypes}
          hardware={[]}
          accessories={[]}
          woodJointOptions={woodJointOptions}
          partsOptions={[]}
          overheadOptions={[]}
          drawingBoardRef={drawingBoardRef}
          pageType="schuifpui"
          onConfigChange={handleConfigChange}
          saveCurrentRef={saveCurrentRef}
          onSaved={(savedConfig) => resetBaseline(savedConfig || config)}
        />
      }
    />
  );
};

export default Schuifpui;

